/**
 * CatalogueCopyBanner (BLU-1528 R3). Where Product sync's lists read from
 * when the org's Shopify catalogue copy is on: the copy's age, an update or
 * re-check in progress, the last update failing, or the copy still loading
 * (the list then reads Shopify directly). Rendered only from the status the
 * page's own reads carry (the POD page, or the PAP walk's pages): no polling,
 * no read of its own.
 *
 * Quiet by default: `NoteLine`s, with a `WarningNotice` box for a failed
 * update only. No glyph spins: only the button's, while its request is sent.
 *
 * "Get latest from Shopify" queues a full export
 * (`productSync.refreshCatalogueCopy`, the page owns the mutation) and shows
 * for an `org_settings.read` holder, the same gate as the procedure. It is
 * ONE button whose words change in place and which is never hidden, so focus
 * stays on it: `aria-disabled` with the reason beside it whenever the server
 * would take nothing (an update already running or requested, or the copy
 * updated under 10 minutes ago, `CATALOGUE_REFRESH_MIN_AGE_MS` on the server).
 * The page holds the last press's answer until the copy's state next changes.
 *
 * A screen reader hears state changes only, from one sr-only live region
 * beside the button (never around it).
 */
import { useEffect, useId, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Clock, Hourglass, ListChecks, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';

export type CatalogueCopyState = 'loading' | 'ready' | 'refreshing' | 'failed';

export interface CatalogueCopyStatus {
  state: CatalogueCopyState;
  refreshedAt: string | null;
  recheckingProducts: number;
}

/** The last press's answer: queued, or nothing queued because an update is already running or requested. */
export type CatalogueRefreshOutcome = 'queued' | 'not_due';

/** Mirrors the server's rule: nothing is queued within 10 minutes of the last complete export. */
export const CATALOGUE_REFRESH_MIN_AGE_MS = 10 * 60_000;

interface CatalogueCopyBannerProps {
  status: CatalogueCopyStatus;
  canRefresh: boolean;
  onRefresh: () => void;
  refreshPending: boolean;
  refreshOutcome: CatalogueRefreshOutcome | null;
  /** For tests; defaults to the current time. */
  now?: number;
}

export function CatalogueCopyBanner({ status, canRefresh, onRefresh, refreshPending, refreshOutcome, now }: CatalogueCopyBannerProps) {
  const { t } = useTranslation('productSync');
  const locale = useDateFnsLocale();
  const messageId = useId();
  const reasonId = useId();
  const refreshedMs = status.refreshedAt ? Date.parse(status.refreshedAt) : Number.NaN;
  const refreshed = Number.isFinite(refreshedMs) ? new Date(refreshedMs) : null;
  const failed = status.state === 'failed';
  const busy = status.state === 'loading' || status.state === 'refreshing';

  // Re-render when the 10 minutes run out, so the button turns back on by itself.
  const [, setTick] = useState(0);
  const refreshedAtMs = refreshed?.getTime() ?? null;
  useEffect(() => {
    if (refreshedAtMs === null || now !== undefined) return undefined;
    const waitMs = refreshedAtMs + CATALOGUE_REFRESH_MIN_AGE_MS - Date.now();
    if (waitMs <= 0) return undefined;
    const timer = window.setTimeout(() => setTick((n) => n + 1), waitMs + 1_000);
    return () => window.clearTimeout(timer);
  }, [refreshedAtMs, now]);
  const tooSoon = refreshed !== null && (now ?? Date.now()) - refreshed.getTime() < CATALOGUE_REFRESH_MIN_AGE_MS;

  let message: string;
  if (status.state === 'loading') message = t('catalogueCopy.loading');
  else if (status.state === 'refreshing') message = t('catalogueCopy.refreshing');
  else if (failed) message = t('catalogueCopy.failed');
  else if (refreshed) message = t('catalogueCopy.ready', { when: formatDistanceToNow(refreshed, { addSuffix: true, locale }) });
  else message = t('catalogueCopy.loading');

  // Why the button takes nothing now. A busy copy's own message says why.
  let reason: string | null = null;
  if (refreshOutcome === 'not_due') reason = t('catalogueCopy.alreadyRunning');
  else if (refreshOutcome === null && !busy && tooSoon) reason = t('catalogueCopy.tooSoon');
  const blocked = refreshPending || refreshOutcome !== null || busy || tooSoon;

  // State changes only: never the first render, never the minutes ticking by.
  const announceKey = `${status.state}|${refreshOutcome ?? ''}`;
  const [announced, setAnnounced] = useState({ key: announceKey, text: '' });
  if (announced.key !== announceKey) {
    const text =
      refreshOutcome === 'queued'
        ? t('catalogueCopy.getLatestQueued')
        : refreshOutcome === 'not_due'
          ? t('catalogueCopy.alreadyRunning')
          : message;
    setAnnounced({ key: announceKey, text });
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <div className="min-w-0 flex-1 basis-64 space-y-1">
        {failed ? (
          <WarningNotice>
            <span id={messageId}>{message}</span>
          </WarningNotice>
        ) : (
          <NoteLine icon={busy ? Hourglass : Clock} id={messageId}>
            {message}
          </NoteLine>
        )}
        {!busy && status.recheckingProducts > 0 && (
          <NoteLine icon={ListChecks}>{t('catalogueCopy.rechecking', { count: status.recheckingProducts })}</NoteLine>
        )}
        {canRefresh && reason && <NoteLine id={reasonId}>{reason}</NoteLine>}
      </div>
      {canRefresh && (
        <Button
          variant="outline"
          size="sm"
          onClick={blocked ? undefined : onRefresh}
          aria-disabled={blocked || undefined}
          aria-describedby={blocked ? (reason ? reasonId : messageId) : undefined}
          className={cn('h-8 shrink-0 gap-1.5 px-3 text-xs', blocked && 'cursor-not-allowed opacity-60')}
        >
          {refreshPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {refreshOutcome === 'queued' ? t('catalogueCopy.getLatestQueued') : t('catalogueCopy.getLatest')}
        </Button>
      )}
      <span className="sr-only" role="status" aria-live="polite">
        {announced.text}
      </span>
    </div>
  );
}
